import type { Viewer } from '../../viewer/Viewer';
import type { MeasureClosestPointResult } from '../types/measure-distance-types';
import type { MeasureEntityRef } from '../types/measure-types';
import {
    MeasurePairReferenceMode,
    type MeasureReferencePolicy,
} from '../types/measure-reference-policy-types';
import { MeasureArcCircleReferenceStrategy } from './MeasureArcCircleReferenceStrategy';
import { MeasureClosestPointReferenceStrategy } from './MeasureClosestPointReferenceStrategy';
import { MeasurePointToPointReferenceStrategy } from './MeasurePointToPointReferenceStrategy';

/**
 * 双选参考点求解门面。
 *
 * 职责：按 {@link MeasureReferencePolicy} 分派到具体策略，统一输出 {@link MeasureClosestPointResult}。
 */
export class MeasurePairReferencePointResolver {
    /** 全局最近点策略。 */
    private readonly closestPointStrategy: MeasureClosestPointReferenceStrategy;

    /** 点到点策略。 */
    private readonly pointToPointStrategy: MeasurePointToPointReferenceStrategy;

    /** 圆弧/圆策略。 */
    private readonly arcCircleStrategy: MeasureArcCircleReferenceStrategy;

    /**
     * @param viewer Viewer 实例。
     */
    public constructor(viewer: Viewer) {
        this.closestPointStrategy = new MeasureClosestPointReferenceStrategy(viewer);
        this.pointToPointStrategy = new MeasurePointToPointReferenceStrategy();
        this.arcCircleStrategy = new MeasureArcCircleReferenceStrategy(viewer);
    }

    /**
     * 求解双选参考点对。
     *
     * @param firstEntity 先选实体（`from` 侧）。
     * @param secondEntity 后选实体（`to` 侧）。
     * @param policy 参考点策略。
     * @param pickHistory 测量点击历史（点到点策略使用）。
     * @returns 参考点结果；无法求解时返回 null。
     */
    public resolve(
        firstEntity: MeasureEntityRef,
        secondEntity: MeasureEntityRef,
        policy: MeasureReferencePolicy,
        pickHistory: readonly MeasureEntityRef[],
    ): MeasureClosestPointResult | null {
        switch (policy.pairMode) {
            case MeasurePairReferenceMode.PointToPoint:
                return this.pointToPointStrategy.resolve(firstEntity, secondEntity, pickHistory);
            case MeasurePairReferenceMode.ArcCircle:
                return this.arcCircleStrategy.resolve(firstEntity, secondEntity, policy);
            case MeasurePairReferenceMode.ClosestPoints:
            default:
                return this.closestPointStrategy.resolve(firstEntity, secondEntity);
        }
    }
}
